import { Form, Layout, TabsProps } from 'antd';
import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useAppDispatch } from 'store/hooks';
import aptActions from 'services/actions/apt';
import aptSelectors from 'services/selectors/apt';
import TabNavigators from './TabNavigators';
import CallerListTab from './CallerListTab';
import CallerDetailTab from './CallerDetailTab';
import CallerPanelHeader from './CallerPanelHeader';
import FloatingButton from './FloatingButton';
import { setCallerSelectActive } from 'Calendar/caller-booking-selection';
import { CALLER_COLORS as C } from './tokens';

type ICallerV2Props = {};
type ICallerV2Ref = {
  toggle: () => void;
  open: () => void;
  close: () => void;
};
export const CallerV2Ref = React.createRef<ICallerV2Ref>();
const _CallerV2Context = forwardRef<ICallerV2Ref, ICallerV2Props>((props, ref) => {
  const [open, setOpen] = useState(false);
  // Bumped on every open() so a fresh trigger (socket/native) refetches the pending
  // list even while the panel is already open.
  const [refreshKey, setRefreshKey] = useState(0);
  const dispatch = useAppDispatch();
  const pendingCount = aptSelectors.pendingCallers.data().length;
  const handleClose = useCallback<ICallerV2Ref['close']>(() => setOpen(false), []);
  const [navigatorForm] = Form.useForm();
  const activeTab = Form.useWatch('activeTab', navigatorForm);

  // Direct-on-calendar range picking is only live while the caller detail tab is
  // open (panel open + detail tab). Leaving it (list tab / close) restores the
  // calendar's normal click behaviour and clears any highlight.
  useEffect(() => {
    setCallerSelectActive(open && activeTab === 'caller-detail');
    return () => setCallerSelectActive(false);
  }, [open, activeTab]);

  const handleToggle: ICallerV2Ref['toggle'] = () => {
    setOpen((prev) => !prev);
    setRefreshKey((k) => k + 1);
  };

  const handleOpen: ICallerV2Ref['open'] = () => {
    setOpen(true);
    setRefreshKey((k) => k + 1);
  };

  useImperativeHandle(ref, () => ({
    toggle: handleToggle,
    open: handleOpen,
    close: handleClose,
  }));

  // Fetch the pending caller list whenever the panel opens or a new trigger arrives.
  useEffect(() => {
    if (open) dispatch(aptActions.getPendingCallers.fetch(undefined));
  }, [open, refreshKey, dispatch]);

  const items: TabsProps['items'] = useMemo(() => [
    {
      key: 'caller-list',
      label: '',
      children: <CallerListTab />,
    },
    {
      key: 'caller-detail',
      label: '',
      children: <CallerDetailTab />,
    },
  ], []);

  return (
    <Layout.Sider collapsed={!open} width="500px" style={siderStyle} collapsedWidth={0}>
      <PanelShell>
        <CallerPanelHeader count={pendingCount} onClose={handleClose} />
        <TabsScroll>
          <Form form={navigatorForm} initialValues={{ activeTab: 'caller-list' }} style={formStyle}>
            <Form.Item noStyle name={'activeItem'}/>
            <Form.Item noStyle name={'activeTab'}>
              <TabNavigators items={items} />
            </Form.Item>
          </Form>
        </TabsScroll>
      </PanelShell>
    </Layout.Sider>
  );
});

_CallerV2Context.displayName = 'CallerV2Context';
const CallerV2Context = memo(_CallerV2Context);

// FloatingButton reads the live pending count from the store so the badge stays
// in sync after each fetch (it's rendered inside the redux Provider in BookingPage).
const CallerV2FloatingButton = () => {
  const count = aptSelectors.pendingCallers.data().length;
  return <FloatingButton count={count} onClick={() => CallerV2Ref.current?.toggle()} />;
};

const CallerV2 = {
  Portal: () => <CallerV2Context ref={CallerV2Ref} />,
  FloatingButton: CallerV2FloatingButton,
  toggle: () => CallerV2Ref.current?.toggle(),
  open: () => CallerV2Ref.current?.open(),
  close: () => CallerV2Ref.current?.close(),
};

export default CallerV2;

const siderStyle: React.CSSProperties = {
  background: '#fff',
};

// Form fills the tab area so the active tab can manage its own height (header
// fixed, content scrolls, detail footer pinned).
const formStyle: React.CSSProperties = {
  flex: '1 1 auto',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
};

// Panel fills the Sider height; header pins to the top and the tab content scrolls.
const PanelShell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
`;

// Bounded flex area for the tabs; each tab scrolls internally (not the panel),
// so the detail tab's footer stays pinned to the bottom.
const TabsScroll = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: ${C.panelBg};
`;
